import { useAuth, useUser } from "@clerk/clerk-react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CircularProgress from "@mui/material/CircularProgress";
import { type FormEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  AuthAltLink,
  AuthCard,
  AuthDivider,
  AuthFloatingField,
  AuthSocialButtons,
} from "@/components/auth";
import { useSignUpFlow } from "@/hooks/useSignUpFlow";
import type { SignUpMethod } from "@/interfaces/analytics";
import { PAGE_VIEW_STATE, pushSignUpStart } from "@/lib/analytics";

// Wait for the backend user.created webhook to finish writing to the local DB
// and updating Clerk's metadata. We know it's fully done when publicMetadata.role is set.
async function pollForWebhookCompletion(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user: any,
  maxRetries = 15,
  delayMs = 1000,
): Promise<boolean> {
  for (let i = 0; i < maxRetries; i++) {
    try {
      await user.reload();
      if (user.publicMetadata?.role) {
        return true; // Success! The webhook has completely finished updating Clerk.
      }
    } catch (e: unknown) {
      console.error("[SignUp] user.reload() failed during polling:", e);
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
  return false; // Timed out
}

/**
 * SignUpPage — custom passwordless (OTP) sign-up matching the designer's demo.
 *
 * Step 1: user enters work email → "Continue" creates the Clerk sign-up and
 *         sends a one-time code. Google / Microsoft OAuth buttons are also shown.
 * Step 2: code field revealed, user enters OTP → "Continue" verifies & activates
 *         the session, then redirects to /onboarding.
 *
 * Replaces the previous 10-line Clerk <SignUp /> drop-in component.
 */
export default function SignUpPage() {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Local states for the webhook synchronization step
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  const {
    step,
    email,
    code,
    isLoading,
    error,
    isReady,
    isTicketFlow,
    ticketComplete,
    setEmail,
    setCode,
    sendCode,
    verifyCode,
    resendCode,
    startOAuth,
    goBack,
  } = useSignUpFlow();

  const hasSyncedRef = useRef(false);

  // Once per page view, on the first real interaction: the email field autofocuses, so focus alone
  // would fire on load.
  const signUpStartedRef = useRef(false);
  const trackSignUpStart = (method: SignUpMethod) => {
    if (signUpStartedRef.current) return;
    signUpStartedRef.current = true;
    pushSignUpStart(method);
  };

  // When the ticket-based session is ready, wait for the backend to sync, then navigate.
  useEffect(() => {
    // Only proceed when the ticket is complete AND the React context has fully populated the user.
    if (!ticketComplete || !user || hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    let isMounted = true;
    pollForWebhookCompletion(user).then(async (synced) => {
      if (!isMounted) return;
      if (synced) {
        try {
          sessionStorage.removeItem("gfg_onboarding_state");
        } catch {
          // silent
        }
        // Invited users skip onboarding entirely and go straight to the dashboard.
        navigate("/", { replace: true, state: PAGE_VIEW_STATE });
      } else {
        setSyncError(
          "Your invitation was accepted but the database sync timed out. Please contact support.",
        );
      }
    });

    return () => {
      isMounted = false;
    };
  }, [ticketComplete, user, navigate]);

  // If already signed in (and not mid-verification or syncing), redirect home.
  // Wait for the user to acknowledge any sync error instead of immediately redirecting.
  if (isSignedIn && !isSyncing && !syncError) {
    return <Navigate to="/" replace />;
  }

  // Wait for Clerk to load.
  if (!isReady) {
    return (
      <main className="gfg-auth-screen">
        <CircularProgress aria-label="Signing up" sx={{ color: "var(--color-dark-dust)" }} />
      </main>
    );
  }

  // Invitation ticket flow — show a spinner while the ticket is being consumed.
  // The hook will set ticketComplete=true once the session is active, triggering
  // the useEffect above to navigate away. If the ticket fails, the hook resets
  // to step='email' and populates error, so the user sees a message and the
  // normal sign-up form below.
  if (isTicketFlow) {
    return (
      <main className="gfg-auth-screen">
        <AuthCard>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 16,
              padding: "24px 0",
            }}
          >
            <CircularProgress aria-label="Signing up" sx={{ color: "var(--color-dark-dust)" }} />
            <p className="gfg-auth-sub" style={{ margin: 0 }}>
              Accepting your invitation…
            </p>
          </div>
        </AuthCard>
      </main>
    );
  }

  const handleContinue = async (e: FormEvent) => {
    e.preventDefault();
    if (step === "email") {
      trackSignUpStart("email");
      await sendCode();
      setTimeout(() => codeInputRef.current?.focus(), 100);
    } else {
      setIsSyncing(true);
      setSyncError(null);
      const success = await verifyCode();
      if (success) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const activeUser = (window as any).Clerk?.user;
        if (activeUser) {
          // Wait for the webhook to finish before redirecting to onboarding
          const synced = await pollForWebhookCompletion(activeUser);
          if (synced) {
            try {
              sessionStorage.removeItem("gfg_onboarding_state");
            } catch {
              // silent
            }
            navigate("/onboarding", { replace: true, state: PAGE_VIEW_STATE });
          } else {
            setIsSyncing(false);
            setSyncError(
              "Your account was created but the backend sync timed out. Please refresh the page.",
            );
          }
        } else {
          navigate("/onboarding", { replace: true, state: PAGE_VIEW_STATE });
        }
      } else {
        setIsSyncing(false);
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !isLoading) {
      handleContinue(e as unknown as FormEvent);
    }
  };

  const handleResend = async () => {
    await resendCode();
  };

  return (
    <main className="gfg-auth-screen">
      <AuthCard>
        {step === "verifying" && (
          <button type="button" className="gfg-auth-back" onClick={goBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back
          </button>
        )}

        <h1 className="gfg-auth-title">Create your account</h1>
        <p className="gfg-auth-sub">Start sending impactful gifts in minutes.</p>

        <form onSubmit={handleContinue} onInput={() => trackSignUpStart("email")}>
          <AuthFloatingField
            label="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={step === "verifying"}
            autoFocus={step === "email"}
          />

          {step === "email" && (
            <p className="gfg-auth-hint">
              Use an organization email to easily collaborate with teammates.
            </p>
          )}

          {step === "verifying" && (
            <>
              <AuthFloatingField
                ref={codeInputRef}
                label="Verification code"
                type="text"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <p className="gfg-auth-hint gfg-auth-hint-strong">
                We sent a code to your inbox. If you don't see it within 5 minutes, check your spam
                folder.
              </p>
            </>
          )}

          {(error || syncError) && (
            <p className="gfg-auth-hint" style={{ color: "var(--color-good-wine)", marginTop: 8 }}>
              {syncError || error}
            </p>
          )}

          <button type="submit" className="gfg-auth-btn" disabled={isLoading || isSyncing}>
            {isLoading || isSyncing ? (
              <CircularProgress
                aria-label="Signing up"
                size={18}
                sx={{ color: "var(--color-white)" }}
              />
            ) : (
              "Continue"
            )}
          </button>
        </form>

        {step === "verifying" && (
          <div className="gfg-auth-alt">
            <button type="button" onClick={handleResend} disabled={isLoading || isSyncing}>
              Resend verification code
            </button>
          </div>
        )}

        {step === "email" && (
          <>
            <AuthDivider />
            <AuthSocialButtons
              onGoogle={() => {
                trackSignUpStart("google_sso");
                startOAuth("oauth_google");
              }}
              onMicrosoft={() => {
                trackSignUpStart("microsoft_sso");
                startOAuth("oauth_microsoft");
              }}
              disabled={isLoading || isSyncing}
            />
            <AuthAltLink text="Already have an account?" linkText="Sign in" to="/sign-in" />
          </>
        )}
      </AuthCard>
    </main>
  );
}
